import { useContext } from "react";
import { ShellContext } from "../context";

/**
 * Hook to access the shell's logout notification system.
 *
 * Use `notifyLogoutStarted` before clearing auth state to prevent race conditions
 * where the shell attempts to reload modules during the logout transition.
 *
 * Use `isLoggingOut` to check if a logout is in progress and skip operations
 * that shouldn't run during logout (e.g., token refresh, permission checks).
 *
 * @returns Object containing isLoggingOut state and notifyLogoutStarted callback
 *
 * @example
 * ```tsx
 * const { isLoggingOut, notifyLogoutStarted } = useLogoutNotification();
 *
 * const handleLogout = async () => {
 *   notifyLogoutStarted(); // Call BEFORE clearing auth
 *   clearAuthenticationInfo(setAuthToken, setAuthRefreshToken, setPermissions);
 *   navigate("/login");
 * };
 * ```
 */
export function useLogoutNotification(): {
  /** Whether a logout is currently in progress */
  isLoggingOut: boolean;
  /** Call this before clearing auth state to notify the shell */
  notifyLogoutStarted: () => void;
} {
  const { isLoggingOut, notifyLogoutStarted } = useContext(ShellContext);

  return { isLoggingOut, notifyLogoutStarted };
}
